import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Chip,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LoadingButton } from '@mui/lab';
import { PieChart } from '@mui/x-charts/PieChart';
import moment from 'moment';
import Page from '../../../../components/Page';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../../components/table/DataGridTable';
import PermissionRequired from '../../../errorPages/permissionRequired';
import useSettings from '../../../../hooks/useSettings';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import { dispatch, useSelector } from '../../../../redux/store';
import { getPaymentTypes } from '../../../../redux/slices/financeRedux/paymentTypeRedux';
import { get } from '../../../../inteceptor';
import { accessVerify, utcMoment } from '../../../../utils/common';
import { fCurrency } from '../../../../utils/formatNumber';

const CLOSED_STATUS_LIST = ['Refunded', 'Canceled'];

const formatDateTime = (value) => (value ? utcMoment(value).format('YYYY-MM-DD HH:mm:ss') : '-');

const getStatusColor = (status) => {
  if (status === 'closed') return 'success';
  if (status === 'open') return 'warning';
  return 'default';
};

const getOrderPaymentTotal = (payments = []) => payments?.reduce((sum, payment) => sum + Number(payment?.amount || 0), 0) || 0;

function POSSessionSummary({ drawerData, selectedDrawer, paymentTypes }) {
  const validPosData = useMemo(() => drawerData?.posData?.filter((data) => data?.id) || [], [drawerData]);

  const posDataWithOpening = useMemo(() => {
    if (!drawerData?.posCashDrawersId) return [];
    return [
      ...validPosData,
      {
        id: 'Starting Amount',
        dateTime: selectedDrawer?.dateTime,
        status: 'Opening',
        totalAmount: Number(drawerData?.startingAmount || 0),
        posPayments: [
          {
            amount: Number(drawerData?.startingAmount || 0),
            paymentTypesId: 1,
            id: 1,
            posId: 0,
          },
        ],
      },
    ];
  }, [drawerData, selectedDrawer, validPosData]);

  const paymentSummary = useMemo(() => {
    const breakdown = {};

    posDataWithOpening.forEach((data) => {
      if (CLOSED_STATUS_LIST.includes(data?.status)) return;
      data?.posPayments?.forEach((payment) => {
        const typeId = payment?.paymentTypesId;
        if (!typeId) return;
        if (!breakdown[typeId]) {
          breakdown[typeId] = {
            totalAmount: 0,
            details: [],
          };
        }

        const amount = Number(payment?.amount || 0);
        breakdown[typeId].totalAmount += amount;
        breakdown[typeId].details.push({
          posId: data?.id,
          posStatus: data?.status,
          amount,
          verifierId: payment?.verifierId || '-',
          paymentTypeId: payment?.paymentTypesId,
        });
      });
    });

    return (
      paymentTypes
        ?.map((paymentType) => {
          const paymentTypeData = breakdown?.[paymentType?.id];
          return {
            id: paymentType?.id,
            name: paymentType?.name,
            totalAmount: paymentTypeData?.totalAmount || 0,
            transactionCount: paymentTypeData?.details?.length || 0,
            details: paymentTypeData?.details || [],
            includesOpeningAmount: paymentTypeData?.details?.some((detail) => detail?.posStatus === 'Opening'),
          };
        })
        ?.filter((paymentType) => paymentType?.totalAmount > 0) || []
    );
  }, [paymentTypes, posDataWithOpening]);

  const paymentDataForPieChart = useMemo(
    () =>
      paymentSummary.map((paymentType) => ({
        id: paymentType?.id,
        value: paymentType?.totalAmount,
        label: paymentType?.name,
      })),
    [paymentSummary]
  );

  const totalAmount = paymentSummary?.reduce((sum, paymentType) => sum + paymentType.totalAmount, 0);
  const reconciledOrders = validPosData?.filter((order) => !CLOSED_STATUS_LIST.includes(order?.status)) || [];
  const orderTotal = reconciledOrders?.reduce((sum, order) => sum + Number(order?.totalAmount || 0), 0);

  if (!drawerData?.posCashDrawersId) {
    return (
      <Card sx={{ p: 3 }}>
        <Typography variant='subtitle1'>Select a POS session to view the reconciliation report.</Typography>
      </Card>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card sx={{ p: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent='space-between' spacing={2}>
            <Box>
              <Typography variant='h5'>Session #{drawerData?.posCashDrawersId}</Typography>
              <Typography variant='body2' color='text.secondary'>
                {selectedDrawer?.locationName || '-'} - Opened {formatDateTime(selectedDrawer?.dateTime)}
              </Typography>
            </Box>
            <Chip label={selectedDrawer?.status || '-'} color={getStatusColor(selectedDrawer?.status)} sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }} />
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Typography variant='overline' color='text.secondary'>
                Opened By
              </Typography>
              <Typography variant='subtitle2'>{selectedDrawer?.openedByUserName || '-'}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant='overline' color='text.secondary'>
                Closed By
              </Typography>
              <Typography variant='subtitle2'>{selectedDrawer?.closedByUserName || '-'}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant='overline' color='text.secondary'>
                Opening Amount
              </Typography>
              <Typography variant='subtitle2'>{fCurrency(drawerData?.startingAmount || 0)}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant='overline' color='text.secondary'>
                Closing Amount
              </Typography>
              <Typography variant='subtitle2'>{fCurrency(drawerData?.closingAmount || selectedDrawer?.closingAmount || 0)}</Typography>
            </Grid>
          </Grid>
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card sx={{ p: 3, height: '100%' }}>
          <Typography variant='overline' color='text.secondary'>
            Reconciled Total
          </Typography>
          <Typography variant='h3' sx={{ mt: 1 }}>
            {fCurrency(totalAmount || 0)}
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            Includes opening amount and excludes refunded or canceled orders.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Stack spacing={1}>
            <Stack direction='row' justifyContent='space-between'>
              <Typography variant='body2' color='text.secondary'>
                Order Sales
              </Typography>
              <Typography variant='subtitle2'>{fCurrency(orderTotal || 0)}</Typography>
            </Stack>
            <Stack direction='row' justifyContent='space-between'>
              <Typography variant='body2' color='text.secondary'>
                Order Count
              </Typography>
              <Typography variant='subtitle2'>{reconciledOrders?.length || 0}</Typography>
            </Stack>
          </Stack>
        </Card>
      </Grid>

      <Grid item xs={12} md={8}>
        <Card sx={{ p: 3, height: '100%', minHeight: 360 }}>
          <Typography variant='h6'>Payment Distribution</Typography>
          {paymentDataForPieChart?.length > 0 ? (
            <PieChart
              series={[
                {
                  data: paymentDataForPieChart,
                  innerRadius: 55,
                  outerRadius: 110,
                  paddingAngle: 2,
                  cornerRadius: 4,
                  highlightScope: { faded: 'global', highlighted: 'item' },
                  faded: { innerRadius: 45, additionalRadius: -12 },
                },
              ]}
              height={300}
            />
          ) : (
            <Box sx={{ minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant='body2' color='text.secondary'>
                No payment data is available for this session.
              </Typography>
            </Box>
          )}
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card sx={{ p: 3, height: '100%', maxHeight: '70vh', overflowY: 'auto' }}>
          <Typography variant='h6' sx={{ mb: 2 }}>
            Payment Details
          </Typography>
          {paymentSummary?.length > 0 ? (
            <Box>
              {paymentSummary?.map((paymentType) => (
                <Box key={paymentType?.id} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.100', display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                      <Typography variant='subtitle1'>{paymentType?.name}</Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {paymentType?.transactionCount} entr{paymentType?.transactionCount === 1 ? 'y' : 'ies'}
                        {paymentType?.includesOpeningAmount ? ' - includes opening amount' : ''}
                      </Typography>
                    </Box>
                    <Typography variant='subtitle1' sx={{ whiteSpace: 'nowrap', color: 'primary.main' }}>
                      {fCurrency(paymentType?.totalAmount)}
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size='small'>
                      <TableHead>
                        <TableRow>
                          <TableCell>Order Number</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Verifier ID</TableCell>
                          <TableCell align='right'>Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paymentType?.details?.map((detail, index) => (
                          <TableRow key={`${paymentType?.id}-${detail?.posId}-${index}`} hover>
                            <TableCell>{detail?.posId}</TableCell>
                            <TableCell>{detail?.posStatus}</TableCell>
                            <TableCell>{detail?.verifierId}</TableCell>
                            <TableCell align='right'>{fCurrency(detail?.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant='body2' color='text.secondary'>
              No payment summary is available.
            </Typography>
          )}
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card sx={{ p: 3, height: '100%', maxHeight: '70vh', overflowY: 'auto' }}>
          <Typography variant='h6' sx={{ mb: 2 }}>
            Orders In Session
          </Typography>
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Order No</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align='right'>Order Total</TableCell>
                  <TableCell align='right'>Paid</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {validPosData?.map((order) => (
                  <TableRow key={order?.id} hover>
                    <TableCell>{order?.id}</TableCell>
                    <TableCell>{formatDateTime(order?.dateTime)}</TableCell>
                    <TableCell>{order?.status || '-'}</TableCell>
                    <TableCell align='right'>{fCurrency(order?.totalAmount || 0)}</TableCell>
                    <TableCell align='right'>{fCurrency(getOrderPaymentTotal(order?.posPayments))}</TableCell>
                  </TableRow>
                ))}
                {validPosData?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant='body2' color='text.secondary'>
                        No orders found for this session.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Grid>
    </Grid>
  );
}

export default function POSSessionReconciliationReport() {
  const { themeStretch } = useSettings();
  const { selectedLocation } = useSelector((state) => state.location);
  const { data: paymentTypes } = useSelector((state) => state.paymentTypes);

  const [sessionList, setSessionList] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedSessionData, setSelectedSessionData] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [fromDate, setFromDate] = useState(moment().startOf('month'));
  const [toDate, setToDate] = useState(moment().endOf('day'));

  const fetchSessions = async () => {
    if (!selectedLocation?.id || !fromDate || !toDate) return;

    const response = await get('posDrawers', {
      locationId: selectedLocation?.id,
      fromDate: moment(fromDate).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      toDate: moment(toDate).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      status: selectedStatus,
    });

    if (response?.status) {
      setSessionList(
        response?.data?.map((value) => ({
          ...value,
          day: utcMoment(value?.dateTime).format('YYYY-MM-DD'),
          time: utcMoment(value?.dateTime).format('HH:mm:ss'),
          openingAmount: value?.startingAmount,
          sessionStatus: value?.status,
        })) || []
      );
    }
  };

  const fetchSessionDetails = async (session) => {
    if (!session?.drawerId) return;
    setSelectedSession(session);
    const response = await get('getDrawerPayments', { id: session?.drawerId });
    if (response?.status) {
      setSelectedSessionData(response?.data);
    }
  };

  useEffect(() => {
    dispatch(getPaymentTypes());
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [selectedLocation, selectedStatus]);

  const columns = [
    { accessorKey: 'drawerId', header: 'Session ID' },
    { accessorKey: 'day', header: 'Day' },
    { accessorKey: 'time', header: 'Opened Time' },
    { accessorKey: 'locationName', header: 'Location' },
    { accessorKey: 'openedByUserName', header: 'Opened By' },
    { accessorKey: 'closedByUserName', header: 'Closed By' },
    {
      accessorKey: 'openingAmount',
      header: 'Opening Amount',
      Cell: ({ row }) => fCurrency(row.original.openingAmount || 0),
    },
    {
      accessorKey: 'closingAmount',
      header: 'Closing Amount',
      Cell: ({ row }) => fCurrency(row.original.closingAmount || 0),
    },
    {
      accessorKey: 'sessionStatus',
      header: 'Status',
      Cell: ({ row }) => <Chip label={row.original.sessionStatus || '-'} color={getStatusColor(row.original.sessionStatus)} size='small' />,
    },
  ];

  const [rowSelection, setRowSelection] = useState(false);

  return (
    <Page title='POS Session Reconciliation'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading='POS Session Reconciliation'
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Reports', href: '' }, { name: 'POS Session Reconciliation' }]}
        />

        {accessVerify('FINANCE_POS_CASH_DRAWER_VIEW') ? (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ p: 3 }}>
                <Grid container spacing={2} alignItems='center'>
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label='From Date'
                      value={fromDate}
                      onChange={(newValue) => {
                        if (newValue) setFromDate(moment(newValue));
                      }}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label='To Date'
                      value={toDate}
                      onChange={(newValue) => {
                        if (newValue) setToDate(moment(newValue));
                      }}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel id='session-status-label'>Status</InputLabel>
                      <Select labelId='session-status-label' value={selectedStatus} label='Status' onChange={(event) => setSelectedStatus(event.target.value)}>
                        <MenuItem value='all'>All</MenuItem>
                        <MenuItem value='open'>Open</MenuItem>
                        <MenuItem value='closed'>Closed</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <LoadingButton fullWidth variant='contained' size='large' onClick={fetchSessions}>
                      Load Sessions
                    </LoadingButton>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <DataGridTable
                name='POS Session Reconciliation Sessions'
                data={sessionList}
                column={columns}
                onRowClick={(row) => fetchSessionDetails(row?.original)}
                isRowClickable={true}
                isLoading={false}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
                enableRowSelection={false}
                enableRowActions={false}
                renderRowActionItems={() => []}
              />
            </Grid>

            <Grid item xs={12}>
              <POSSessionSummary drawerData={selectedSessionData} selectedDrawer={selectedSession} paymentTypes={paymentTypes} />
            </Grid>
          </Grid>
        ) : (
          <PermissionRequired />
        )}
      </Container>
    </Page>
  );
}
